import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Avatar, Badge, Button, Chip, Empty, Input, Loading, Select,
} from '../../components/Ui';
import { fileUrl } from '../../api/client';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { dateTime, salaryText, timeAgo, titleCase } from '../../utils/format';

const STATUSES = ['applied', 'shortlisted', 'interview', 'hired', 'rejected'];
const MODES = [
  { value: 'in_person', label: 'At the workplace' },
  { value: 'phone', label: 'On a call' },
  { value: 'video', label: 'On video' },
];

/** Recruiter view: jobs this partner posted and the candidates who applied. */
const PartnerJobsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.jobs.mine({ limit: 40 })
      .then((res) => setJobs(res.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openApplicants = async (job) => {
    setSelected(job);
    try {
      const res = await api.jobs.applications(job._id, { limit: 50 });
      setApplicants(res.data || []);
    } catch (e) { setApplicants([]); }
  };

  const setStatus = async (application, status, interview) => {
    try {
      const res = await api.jobs.setApplicationStatus(application._id, { status, interview });
      setApplicants((list) => list.map((a) => (a._id === application._id
        ? { ...a, status, interview: (res.data && res.data.interview) || a.interview }
        : a)));
      if (interview) setScheduling(null);
    } catch (err) {
      toast.error(err.message, 'Could not update');
    }
  };

  /*
   * Calling someone in is a status change plus the where and when - the
   * candidate cannot act on "interview" alone.
   */
  const [scheduling, setScheduling] = useState(null);
  const [slot, setSlot] = useState({ at: '', mode: 'in_person', place: '', note: '' });
  const sendInterview = (application) => {
    if (!slot.at) { toast.error('Pick a date and time first.'); return; }
    setStatus(application, 'interview', { ...slot, at: new Date(slot.at).toISOString() });
  };

  const toggleJob = async (job) => {
    await api.jobs.close(job._id);
    load();
  };

  if (selected) {
    return (
      <div>
        <PageHeader
          title={selected.title}
          subtitle={`${applicants.length} applicants`}
          onBack={() => { setSelected(null); setApplicants([]); }}
        />

        {!applicants.length ? (
          <Empty emoji="📄" title="No applicants yet" text="Share this job so more people apply." />
        ) : (
          <div className="grid grid-list">
            {applicants.map((item, i) => (
              <Reveal key={item._id} delay={stagger(i, 50)} className="card sh-sm">
                <div className="row" style={{ gap: 12 }}>
                  <Avatar
                    name={item.name || (item.user && item.user.name)}
                    uri={item.user && item.user.avatar}
                    size={46}
                  />
                  <div className="grow">
                    <div className="t-title">
                      {item.name || (item.user && item.user.name) || 'Candidate'}
                    </div>
                    <div className="t-tiny">
                      {item.experienceYears ? `${item.experienceYears} yr experience` : 'Fresher'}
                      {' · applied '}{timeAgo(item.createdAt)}
                    </div>
                  </div>
                  <a
                    href={`tel:${item.phone || (item.user && item.user.phone)}`}
                    className="press"
                    style={{
                      width: 34, height: 34, borderRadius: 10, background: colors.successBg,
                      display: 'grid', placeItems: 'center', flex: '0 0 auto',
                    }}
                    aria-label="Call candidate"
                  >
                    📞
                  </a>
                </div>

                {item.coverLetter ? (
                  <p className="t-small" style={{ marginTop: 12, lineHeight: 1.6 }}>{item.coverLetter}</p>
                ) : null}

                {item.user && item.user.skills && item.user.skills.length ? (
                  <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>
                    {item.user.skills.map((s) => (
                      <span
                        key={s}
                        style={{
                          background: colors.primaryLight, color: colors.primary,
                          padding: '4px 9px', borderRadius: 7, fontSize: 11.5, fontWeight: 600,
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                ) : null}

                {item.resume || (item.answers || []).length ? (
                  <div style={{ marginTop: 10 }}>
                    {item.resume ? (
                      <a className="link-btn" href={fileUrl(item.resume)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                        📄 Open resume
                      </a>
                    ) : null}
                    {(item.answers || []).map((a) => ((a.files || []).length ? (
                      <a key={a.key} className="link-btn" href={fileUrl(a.files[0])} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                        📎 {a.label}
                      </a>
                    ) : (
                      <div key={a.key} className="t-tiny">{a.label}: {a.value || '—'}</div>
                    )))}
                  </div>
                ) : null}

                <div
                  className="row wrap"
                  style={{ gap: 6, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.line}` }}
                >
                  {STATUSES.map((s) => (
                    <Chip
                      key={s}
                      label={titleCase(s)}
                      active={item.status === s}
                      onClick={() => setStatus(item, s)}
                    />
                  ))}
                </div>

                <div className="row wrap" style={{ gap: 14, marginTop: 10 }}>
                  <a
                    className="link-btn"
                    href={`https://wa.me/91${item.phone || (item.user && item.user.phone)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    💬 WhatsApp
                  </a>
                  <button type="button" className="link-btn" onClick={() => setScheduling(scheduling === item._id ? null : item._id)}>
                    {scheduling === item._id ? 'Cancel' : '🗓️ Call for interview'}
                  </button>
                </div>

                {item.interview && item.interview.at && scheduling !== item._id ? (
                  <div className="card" style={{ marginTop: 10, background: colors.bg }}>
                    <div className="t-title">Interview sent</div>
                    <div className="t-small">
                      {dateTime(item.interview.at)}{item.interview.place ? ` · ${item.interview.place}` : ''}
                    </div>
                  </div>
                ) : null}

                {scheduling === item._id ? (
                  <div className="card" style={{ marginTop: 10, background: colors.bg }}>
                    <Input
                      label="Date and time"
                      type="datetime-local"
                      value={slot.at}
                      onChange={(e) => setSlot({ ...slot, at: e.target.value })}
                    />
                    <Select
                      label="Where"
                      value={slot.mode}
                      onChange={(v) => setSlot({ ...slot, mode: v })}
                      options={MODES}
                    />
                    {slot.mode === 'in_person' ? (
                      <Input
                        label="Address"
                        placeholder="Shop / office address"
                        value={slot.place}
                        onChange={(e) => setSlot({ ...slot, place: e.target.value })}
                      />
                    ) : null}
                    <Input
                      label="Message (optional)"
                      placeholder="Bring your documents"
                      value={slot.note}
                      onChange={(e) => setSlot({ ...slot, note: e.target.value })}
                    />
                    <Button title="Send interview details" onClick={() => sendInterview(item)} />
                  </div>
                ) : null}
              </Reveal>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="My jobs"
        back={false}
        subtitle={`${jobs.length} posted`}
        right={<Button title="+ Post a job" size="sm" onClick={() => navigate('/partner/jobs/new')} />}
      />

      {loading ? <Loading /> : !jobs.length ? (
        <Empty
          emoji="💼"
          title="No jobs posted"
          text="Hiring? Post a job and reach candidates in your area."
          actionLabel="Post a job"
          onAction={() => navigate('/partner/jobs/new')}
        />
      ) : (
        <div className="grid grid-cards">
          {jobs.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 50)} className="card sh-sm">
              <div className="row-between" style={{ alignItems: 'flex-start' }}>
                <div className="grow">
                  <div className="t-title">{item.title}</div>
                  <div className="t-tiny">
                    {titleCase(item.jobType)} · {item.city} · posted {timeAgo(item.createdAt)}
                  </div>
                  <div className="t-body" style={{ color: colors.success, fontWeight: 700, marginTop: 6 }}>
                    {salaryText(item)}
                  </div>
                </div>
                <Badge value={item.status} />
              </div>

              <div
                className="row"
                style={{ gap: 14, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.line}` }}
              >
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => openApplicants(item)}
                >
                  {item.applicationCount || 0} applicants →
                </button>
                <span className="grow" />
                <button type="button" className="link-btn" onClick={() => toggleJob(item)}>
                  {item.status === 'open' ? 'Close job' : 'Reopen'}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerJobsPage;
